/**
 * StationManagementDialog — Manage space stations: purchase, deploy, relocate,
 * set fees, view stored asteroids, and check insurance status.
 *
 * Side-by-side layout: station list (left) | detail/confirm panel (right).
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import type { Station, StationType, StationAsteroid, Chain, Stablecoin } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, createDialogChrome, closeDialog, Fonts, FontSize, Radius } from './GameUITheme';
import { InsuranceDialog } from './InsuranceDialog';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import { EventBus } from '../../utils/EventBus';
import { MMOEvents } from '../../managers/MMONetworkManager';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

/* ── Layout ─────────────────────────────────────────────── */
const PAD        = 16;
const LIST_TOP   = 55;

/* ── Station metadata ───────────────────────────────────── */
const STATION_META: Record<string, {
  label: string; color: string; description: string; portrait: string;
  maxHp: number; maxStorage: number;
}> = {
  waypoint: {
    label: 'Waypoint', color: '#00ffff',
    description: I18n.t('station_mgmt.basic_desc'),
    portrait: 'station-waypoint',
    maxHp: 50, maxStorage: 3,
  },
  depot: {
    label: 'Depot', color: '#00ff88',
    description: I18n.t('station_mgmt.mid_desc'),
    portrait: 'station-depot',
    maxHp: 100, maxStorage: 10,
  },
  fortress: {
    label: 'Fortress', color: '#ff8800',
    description: I18n.t('station_mgmt.fort_desc'),
    portrait: 'station-fortress',
    maxHp: 250, maxStorage: 25,
  },
  capital: {
    label: 'Capital', color: '#ff0088',
    description: I18n.t('station_mgmt.capital_desc'),
    portrait: 'station-capital',
    maxHp: 500, maxStorage: 999,
  },
};

const CHAINS: { id: Chain; label: string }[] = [
  { id: 'solana', label: 'Solana' },
  { id: 'ethereum', label: 'Ethereum' },
  { id: 'bsc', label: 'BSC' },
  { id: 'galachain', label: 'GalaChain' },
];
const STABLECOINS: { id: Stablecoin; label: string }[] = [
  { id: 'USDC', label: 'USDC' },
  { id: 'USDT', label: 'USDT' },
];

type DetailView = 'info' | 'purchase-confirm' | 'asteroids' | 'set-fee' | 'minerals' | 'transfer';

export class StationManagementDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private stations: Station[] = [];
  private prices: Record<string, number> = {};
  private selectedStation: Station | null = null;
  private detailView: DetailView = 'info';

  /* purchase-confirm state */
  private purchaseType: StationType = 'waypoint';
  private selectedChain: Chain = 'solana';
  private selectedStablecoin: Stablecoin = 'USDC';
  private isPurchasing = false;

  /* set-fee state */
  private pendingFee = 0;

  /* asteroids cache */
  private cachedAsteroids: StationAsteroid[] = [];

  /* minerals cache */
  private cachedMinerals: { mineralTypeId: string; mineralName: string; displayName: string; amount: number; rarity: string; color: string }[] = [];
  private cachedTransfers: any[] = [];
  private transferTargetId: string | null = null;
  private stationMineralCounts: Map<string, number> = new Map();

  /* auto-refresh timer for minerals view */
  private refreshTimer: Phaser.Time.TimerEvent | null = null;

  /* insurance cache */
  private insuranceStatus: { insured: boolean; expiresAt?: string } | null = null;

  /* UI containers */
  private listContainer!: Phaser.GameObjects.Container;
  private listContent!: Phaser.GameObjects.Container;
  private detailContainer!: Phaser.GameObjects.Container;
  private statusText!: Phaser.GameObjects.Text;
  private scrollTrack!: Phaser.GameObjects.Graphics;
  private scrollThumb!: Phaser.GameObjects.Graphics;
  private scrollY = 0;
  private maxScrollY = 0;
  private totalContentH = 0;

  /* responsive layout */
  private mobile: boolean;
  private listW: number;
  private detailW: number;
  private scrollH: number;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 660;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;

    const LIST_W = mobile ? dw - PAD * 2 : 252;
    const DETAIL_X = LIST_W + PAD * 2 + 10;
    this.listW = LIST_W;
    this.detailW = mobile ? dw - PAD * 2 : dw - DETAIL_X - PAD;
    this.scrollH = dh - LIST_TOP - 30;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.stations'),
      onClose: () => this.close(),
    });

    /* ── List (left) ─────────────────────────────────────── */
    this.listContainer = scene.add.container(PAD, LIST_TOP);
    this.add(this.listContainer);

    this.listContent = scene.add.container(0, 0);
    this.listContainer.add(this.listContent);

    // Scroll mask
    const maskGfx = scene.make.graphics({});
    maskGfx.fillRect(this.x + PAD, this.y + LIST_TOP, LIST_W, this.scrollH);
    maskGfx.setVisible(false);
    this.listContent.setMask(new Phaser.Display.Masks.GeometryMask(scene, maskGfx));

    /* ── Divider + scrollbar ─────────────────────────────── */
    const divX = LIST_W + PAD + 4;
    const divider = scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.06);
    divider.lineBetween(divX, LIST_TOP, divX, dh - 30);
    this.add(divider);

    this.scrollTrack = scene.add.graphics();
    this.scrollTrack.setPosition(divX - 1, LIST_TOP);
    this.add(this.scrollTrack);

    this.scrollThumb = scene.add.graphics();
    this.scrollThumb.setPosition(divX - 1, LIST_TOP);
    this.add(this.scrollThumb);

    /* ── Detail (right / overlaid on mobile) ─────────────── */
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, LIST_TOP);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    /* ── Status text ─────────────────────────────────────── */
    this.statusText = scene.add.text(dw / 2, dh - 18, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      wordWrap: { width: dw - 40 },
    }).setOrigin(0.5, 0.5);
    this.add(this.statusText);

    /* ── Wheel scroll ────────────────────────────────────── */
    scene.input.on('wheel', (_p: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      if (!this.visible || this.maxScrollY <= 0) return;
      this.scrollY = Phaser.Math.Clamp(this.scrollY - dy * 0.5, -this.maxScrollY, 0);
      this.listContent.y = this.scrollY;
      this.updateScrollbar();
    });

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    this.stopRefreshTimer();
    closeDialog(this.scene, this);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      this.refreshTimer.remove();
      this.refreshTimer = null;
    }
  }

  /* ================================================================
     DATA
     ================================================================ */

  private async loadData(): Promise<void> {
    this.setStatus('Loading...');
    const [stations, prices, breakdown] = await Promise.all([
      this.monetization.fetchStations(),
      this.monetization.fetchStationPrices(),
      this.monetization.fetchMineralInventoryBreakdown(),
    ]);
    this.stations = stations;
    this.prices = prices;

    // Build mineral counts per station from the breakdown
    this.stationMineralCounts.clear();
    for (const loc of breakdown.locations) {
      if (loc.locationType === 'station') {
        const total = loc.minerals.reduce((sum, m) => sum + m.amount, 0);
        this.stationMineralCounts.set(loc.locationId, Math.floor(total));
      }
    }

    // Re-select if we had one
    if (this.selectedStation) {
      this.selectedStation = this.stations.find(s => s.id === this.selectedStation!.id) || this.stations[0] || null;
    } else if (this.stations.length > 0) {
      this.selectedStation = this.stations[0];
    }

    // Fetch insurance for selected station
    if (this.selectedStation?.status === 'active' || this.selectedStation?.status === 'relocating') {
      this.loadInsurance(this.selectedStation.id);
    }

    this.setStatus('');
    this.detailView = 'info';
    this.renderList();
    this.renderDetail();
  }

  private async loadInsurance(stationId: string): Promise<void> {
    try {
      const data = await this.monetization.fetchInsuranceStatus(stationId);
      this.insuranceStatus = data.insurance
        ? { insured: true, expiresAt: (data.insurance as any).coverageExpiresAt }
        : { insured: false };
    } catch {
      this.insuranceStatus = null;
    }
  }

  private setStatus(msg: string): void { this.statusText.setText(msg); }

  /* ================================================================
     SCROLLBAR
     ================================================================ */

  private updateScrollbar(): void {
    this.scrollTrack.clear();
    this.scrollThumb.clear();
    if (this.maxScrollY <= 0) return;

    const TRACK_W = 3;
    this.scrollTrack.fillStyle(0xffffff, 0.04);
    this.scrollTrack.fillRoundedRect(0, 0, TRACK_W, this.scrollH, 1);

    const visibleRatio = this.scrollH / this.totalContentH;
    const thumbH = Math.max(24, Math.round(this.scrollH * visibleRatio));
    const scrollProgress = this.maxScrollY > 0 ? -this.scrollY / this.maxScrollY : 0;
    const thumbY = Math.round(scrollProgress * (this.scrollH - thumbH));

    this.scrollThumb.fillStyle(Colors.cyan, 0.25);
    this.scrollThumb.fillRoundedRect(0, thumbY, TRACK_W, thumbH, 1);
  }

  /* ================================================================
     LEFT — List
     ================================================================ */

  private renderList(): void {
    this.listContent.removeAll(true);
    let y = 0;

    /* ── Purchase section ────────────────────────────────── */
    this.listContent.add(this.scene.add.text(0, y, 'PURCHASE', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 20;

    const types: StationType[] = ['waypoint', 'depot', 'fortress', 'capital'];
    for (const t of types) {
      const meta = STATION_META[t];
      const price = this.prices[t] ?? 0;
      const CARD_H = 54;

      const card = this.scene.add.container(0, y);
      const bg = this.scene.add.graphics();
      const drawBg = (hover: boolean) => {
        bg.clear();
        bg.fillStyle(hover ? 0x1e2844 : 0x151525, 0.8);
        bg.fillRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
        bg.lineStyle(1, Colors.cyan, hover ? 0.15 : 0.06);
        bg.strokeRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
      };
      drawBg(false);
      card.add(bg);

      // Name
      card.add(this.scene.add.text(10, 6, meta.label, {
        fontSize: FontSize.sm, color: meta.color, fontFamily: Fonts.heading,
      }));

      // Price (top right)
      card.add(this.scene.add.text(this.listW - 10, 6, `$${price.toLocaleString()}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
      }).setOrigin(1, 0));

      // HP and Storage stats
      card.add(this.scene.add.text(10, 24, `${I18n.t('station_mgmt.hp')} ${meta.maxHp.toLocaleString()}`, {
        fontSize: FontSize.xs, color: '#778899', fontFamily: Fonts.body,
      }));
      card.add(this.scene.add.text(100, 24, `${I18n.t('station_mgmt.storage')} ${meta.maxStorage}`, {
        fontSize: FontSize.xs, color: '#778899', fontFamily: Fonts.body,
      }));

      // Description
      card.add(this.scene.add.text(10, 40, meta.description.split('.')[0], {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
      }));

      const zone = this.scene.add.zone(0, 0, this.listW, CARD_H).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, CARD_H), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerover', () => { drawBg(true); this.scene.input.setDefaultCursor('pointer'); });
      zone.on('pointerout', () => { drawBg(false); this.scene.input.setDefaultCursor('default'); });
      zone.on('pointerdown', () => {
        this.purchaseType = t;
        this.detailView = 'purchase-confirm';
        this.renderDetail();
        if (this.mobile) {
          this.listContainer.setVisible(false);
          this.detailContainer.setVisible(true);
        }
      });
      card.add(zone);

      this.listContent.add(card);
      y += CARD_H + 5;
    }

    y += 14;

    /* ── Owned stations ──────────────────────────────────── */
    const ownedLabel = this.stations.length > 0
      ? `${I18n.t('station_mgmt.owned_stations')}: ${this.stations.length}`
      : I18n.t('station_mgmt.owned_stations');
    this.listContent.add(this.scene.add.text(0, y, ownedLabel, {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 20;

    if (this.stations.length === 0) {
      const emptyContainer = this.scene.add.container(0, y);
      const emptyBg = this.scene.add.graphics();
      emptyBg.fillStyle(0x151525, 0.5);
      emptyBg.fillRoundedRect(0, 0, this.listW, 60, Radius.sm);
      emptyBg.lineStyle(1, Colors.cyan, 0.04);
      emptyBg.strokeRoundedRect(0, 0, this.listW, 60, Radius.sm);
      emptyContainer.add(emptyBg);

      emptyContainer.add(this.scene.add.text(this.listW / 2, 16, I18n.t('station_mgmt.no_stations'), {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }).setOrigin(0.5, 0));
      emptyContainer.add(this.scene.add.text(this.listW / 2, 36, I18n.t('station_mgmt.get_started'), {
        fontSize: FontSize.xs, color: '#445566', fontFamily: Fonts.body,
      }).setOrigin(0.5, 0));

      this.listContent.add(emptyContainer);
      y += 68;
    } else {
      for (const station of this.stations) {
        const CARD_H = 68;
        const isSelected = this.selectedStation?.id === station.id;
        const meta = STATION_META[station.stationType] || { label: station.stationType, color: '#ffffff', description: '', portrait: '', maxHp: 100, maxStorage: 3 };
        const card = this.scene.add.container(0, y);

        const bg = this.scene.add.graphics();
        const drawBg = (selected: boolean, hover: boolean) => {
          bg.clear();
          const fill = selected ? Colors.bgElevated : hover ? Colors.bgCardHover : Colors.bgCard;
          bg.fillStyle(fill, selected ? 0.9 : 0.8);
          bg.fillRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
          bg.lineStyle(selected ? 2 : 1, Colors.cyan, selected ? 0.4 : hover ? 0.15 : 0.06);
          bg.strokeRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
        };
        drawBg(isSelected, false);
        card.add(bg);

        // Name (top left)
        card.add(this.scene.add.text(10, 7, meta.label, {
          fontSize: FontSize.sm, color: meta.color, fontFamily: Fonts.heading,
        }));

        // Fee badge (top right, prominent)
        card.add(this.scene.add.text(this.listW - 10, 7, `${station.stationFeePct}% Fee`, {
          fontSize: FontSize.sm, color: '#aaccff', fontFamily: Fonts.heading,
        }).setOrigin(1, 0));

        // Status + coordinates
        let statusLabel = station.status.toUpperCase();
        if (station.status === 'active' && station.positionQ !== null) {
          statusLabel = `ACTIVE · (${station.positionQ}, ${station.positionR})`;
        }
        card.add(this.scene.add.text(10, 26, statusLabel, {
          fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
        }));

        // HP + Minerals on the same row
        const hpColor = station.currentHp === station.maxHp ? '#44ff88'
          : station.currentHp > station.maxHp * 0.5 ? '#aaccdd'
          : station.currentHp > station.maxHp * 0.25 ? '#ffaa00' : '#ff4444';
        card.add(this.scene.add.text(10, 46, `HP ${station.currentHp.toLocaleString()}/${station.maxHp.toLocaleString()}`, {
          fontSize: FontSize.xs, color: hpColor, fontFamily: Fonts.body,
        }));

        const mineralCount = this.stationMineralCounts.get(station.id) || 0;
        const mineralColor = mineralCount > 0 ? '#55cc88' : '#445566';
        card.add(this.scene.add.text(this.listW - 10, 46, mineralCount > 0 ? `⛏ ${mineralCount}` : '⛏ 0', {
          fontSize: FontSize.xs, color: mineralColor, fontFamily: Fonts.body,
        }).setOrigin(1, 0));

        // Zone hitbox
        const zone = this.scene.add.zone(0, 0, this.listW, CARD_H).setOrigin(0, 0);
        zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, CARD_H), Phaser.Geom.Rectangle.Contains);
        zone.on('pointerover', () => {
          if (!isSelected) drawBg(false, true);
          this.scene.input.setDefaultCursor('pointer');
        });
        zone.on('pointerout', () => {
          drawBg(isSelected, false);
          this.scene.input.setDefaultCursor('default');
        });
        zone.on('pointerdown', async () => {
          this.selectedStation = station;
          this.detailView = 'info';
          this.insuranceStatus = null;
          this.renderList();
          this.renderDetail();
          if (this.mobile) {
            this.listContainer.setVisible(false);
            this.detailContainer.setVisible(true);
          }
          if (station.status === 'active') {
            await this.loadInsurance(station.id);
            if (this.selectedStation?.id === station.id) this.renderDetail();
          }
        });
        card.add(zone);

        this.listContent.add(card);
        y += CARD_H + 6;
      }
    }

    this.totalContentH = y;
    this.maxScrollY = Math.max(0, y - this.scrollH);
    this.updateScrollbar();
  }

  /* ================================================================
     RIGHT — Detail views
     ================================================================ */

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (this.detailView !== 'minerals') {
      this.stopRefreshTimer();
    }

    switch (this.detailView) {
      case 'info':             this.renderInfoView(); break;
      case 'purchase-confirm': this.renderPurchaseConfirm(); break;
      case 'asteroids':        this.renderAsteroidsView(); break;
      case 'set-fee':          this.renderSetFeeView(); break;
      case 'minerals':         this.renderMineralsView(); break;
      case 'transfer':         this.renderTransferView(); break;
    }
  }

  /* ── Info view ─────────────────────────────────────────── */

  private renderInfoView(): void {
    if (!this.selectedStation) {
      this.detailContainer.add(this.scene.add.text(this.detailW / 2, 60, 'Select a station\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      }).setOrigin(0.5, 0));
      return;
    }

    const station = this.selectedStation;
    const meta = STATION_META[station.stationType] || { label: station.stationType, color: '#ffffff', description: '', portrait: '' };
    let y = 0;

    // Mobile back button to return to list
    if (this.mobile) {
      this.detailContainer.add(this.createMobileBackBtn());
      y += 30;
    }

    /* Portrait */
    if (meta.portrait && this.scene.textures.exists(meta.portrait)) {
      const portrait = this.scene.add.image(this.detailW / 2, 56, meta.portrait);
      portrait.setDisplaySize(96, 96);
      this.detailContainer.add(portrait);
      y = 116;
    }

    /* Name + description */
    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, meta.label, {
      fontSize: FontSize.xl, color: meta.color, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 24;

    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, meta.description, {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      wordWrap: { width: this.detailW - 10 },
    }).setOrigin(0.5, 0));
    y += 22;

    this.addDivider(y); y += 10;

    /* Stats with dotted leaders */
    this.detailContainer.add(this.scene.add.text(0, y, 'STATION STATS', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    // Status
    const statusStr = station.status === 'relocating' && station.positionQ !== null
      ? `Relocating (${station.positionQ}, ${station.positionR})`
      : station.status === 'active' && station.positionQ !== null
        ? `Active (${station.positionQ}, ${station.positionR})`
        : station.status.charAt(0).toUpperCase() + station.status.slice(1);
    const statusColor = station.status === 'active' ? '#44ff88' : station.status === 'relocating' ? '#ffcc00' : station.status === 'destroyed' ? '#ff4444' : '#aaccdd';
    y = this.addStatRow(0, y, 'Status', statusStr, statusColor);

    // HP
    const hpColor = station.currentHp === station.maxHp ? '#44ff88'
      : station.currentHp > station.maxHp * 0.5 ? '#aaccdd'
      : station.currentHp > station.maxHp * 0.25 ? '#ffaa00' : '#ff4444';
    y = this.addStatRow(0, y, 'Hit Points', `${station.currentHp.toLocaleString()} / ${station.maxHp.toLocaleString()}`, hpColor);

    // Storage
    const storageColor = station.storedAsteroids >= station.maxStorage ? '#ff4444' : '#ffd700';
    y = this.addStatRow(0, y, 'Storage', `${station.storedAsteroids} / ${station.maxStorage}`, storageColor);

    // Minerals
    const mineralCount = this.stationMineralCounts.get(station.id) || 0;
    const mineralInfoColor = mineralCount > 0 ? '#55cc88' : '#556677';
    y = this.addStatRow(0, y, 'Minerals', mineralCount > 0 ? mineralCount.toLocaleString() : 'None', mineralInfoColor);

    y = this.addStatRow(0, y, 'Fee', `${station.stationFeePct}%`, '#aaccff');

    if (station.placedAt) {
      const placedDate = new Date(station.placedAt).toLocaleDateString();
      y = this.addStatRow(0, y, 'Deployed', placedDate, '#889999');
    }

    // Insurance status
    if (station.status === 'active' || station.status === 'relocating') {
      if (this.insuranceStatus === null) {
        y = this.addStatRow(0, y, 'Insurance', 'Loading...', '#667788');
      } else if (this.insuranceStatus.insured) {
        y = this.addStatRow(0, y, 'Insurance', 'INSURED ✓', '#44ff88');
      } else {
        y = this.addStatRow(0, y, 'Insurance', 'NOT INSURED', '#ff4444');
      }
    }

    y += 6;
    this.addDivider(y); y += 10;

    /* Action buttons */
    if (station.status === 'inventory') {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 36, 'Deploy Station', 0x115522, 0x22aa44, () => {
        this.handleDeploy();
      }));
      y += 44;
    }

    if (station.status === 'active' || station.status === 'relocating') {
      // View Minerals
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'View Minerals', 0x2a3322, 0x558844, async () => {
        this.setStatus('Loading minerals...');
        this.cachedMinerals = await this.monetization.fetchLocationMinerals('station', station.id);
        this.cachedTransfers = await this.monetization.fetchMineralTransfers();
        this.setStatus('');
        this.detailView = 'minerals';
        this.renderDetail();
      }));
      y += 40;

      // View Asteroids
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'View Stored Asteroids', 0x443300, 0x886600, async () => {
        this.setStatus('Loading asteroids...');
        this.cachedAsteroids = await this.monetization.fetchStationAsteroids(station.id);
        this.setStatus('');
        this.detailView = 'asteroids';
        this.renderDetail();
      }));
      y += 40;

      // Set Fee
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, `Set Fee (${station.stationFeePct}%)`, 0x333355, 0x445577, () => {
        this.pendingFee = station.stationFeePct;
        this.detailView = 'set-fee';
        this.renderDetail();
      }));
      y += 40;

      // Toggle Shipyard
      const hasShipyard = station.hasShipyard ?? false;
      const shipyardLabel = hasShipyard ? 'Disable Shipyard' : 'Enable Shipyard';
      const shipyardBg = hasShipyard ? 0x553322 : 0x224455;
      const shipyardBorder = hasShipyard ? 0xaa6633 : 0x4488aa;
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, shipyardLabel, shipyardBg, shipyardBorder, () => {
        EventBus.emit('send_message', {
          type: 'mmo_toggle_shipyard',
          payload: { stationId: station.id, enabled: !hasShipyard },
        });
        this.setStatus(hasShipyard ? 'Disabling shipyard...' : 'Enabling shipyard...');
        const handler = (data: any) => {
          EventBus.off(MMOEvents.SHIPYARD_TOGGLED, handler);
          if (data?.success) {
            station.hasShipyard = data.enabled;
            this.setStatus(`Shipyard ${data.enabled ? 'enabled' : 'disabled'}`);
            this.renderDetail();
          } else {
            this.setStatus(data?.error || 'Failed to toggle shipyard');
          }
        };
        EventBus.on(MMOEvents.SHIPYARD_TOGGLED, handler);
      }));
      y += 40;

      // Relocate
      if (station.status === 'relocating') {
        this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Relocating...', 0x222233, 0x333344, () => {}));
      } else {
        this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Relocate', 0x333355, 0x445577, () => {
          EventBus.emit('station-relocate-requested', { stationId: station.id });
          this.close();
        }));
      }
      y += 40;

      // Insurance CTA
      if (this.insuranceStatus && !this.insuranceStatus.insured) {
        this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Get Insurance', 0x552211, 0xaa4422, () => {
          this.close();
          new InsuranceDialog(this.scene);
        }));
        y += 40;
      }
    }
  }

  /* ── Purchase confirm ──────────────────────────────────── */

  private renderPurchaseConfirm(): void {
    const type = this.purchaseType;
    const meta = STATION_META[type];
    const price = this.prices[type] ?? 0;
    let y = 0;

    /* Back */
    this.detailContainer.add(this.createSmallBtn(0, y, 90, 28, '← Back', () => {
      this.detailView = 'info';
      this.isPurchasing = false;
      this.renderDetail();
    }));
    y += 38;

    /* Header */
    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, 'CONFIRM PURCHASE', {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 26;

    this.addDivider(y); y += 10;

    /* Station summary */
    if (meta.portrait && this.scene.textures.exists(meta.portrait)) {
      const icon = this.scene.add.image(24, y + 24, meta.portrait);
      icon.setDisplaySize(44, 44);
      this.detailContainer.add(icon);
    }

    this.detailContainer.add(this.scene.add.text(54, y, meta.label, {
      fontSize: FontSize.lg, color: meta.color, fontFamily: Fonts.heading,
    }));
    this.detailContainer.add(this.scene.add.text(54, y + 22, meta.description, {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      wordWrap: { width: this.detailW - 60 },
    }));
    y += 58;

    this.addDivider(y); y += 10;

    /* Chain selector */
    this.detailContainer.add(this.scene.add.text(0, y, 'BLOCKCHAIN', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    let cx = 0;
    const chainBtnW = Math.floor((this.detailW - (CHAINS.length - 1) * 6) / CHAINS.length);
    for (const chain of CHAINS) {
      const isActive = chain.id === this.selectedChain;
      this.detailContainer.add(this.createToggleBtn(cx, y, chainBtnW, 28, chain.label, isActive, () => {
        this.selectedChain = chain.id;
        this.renderDetail();
      }));
      cx += chainBtnW + 6;
    }
    y += 38;

    /* Stablecoin selector */
    this.detailContainer.add(this.scene.add.text(0, y, 'PAY WITH', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    let sx = 0;
    const coinBtnW = Math.floor((this.detailW - (STABLECOINS.length - 1) * 6) / STABLECOINS.length);
    for (const coin of STABLECOINS) {
      const isActive = coin.id === this.selectedStablecoin;
      this.detailContainer.add(this.createToggleBtn(sx, y, coinBtnW, 28, coin.label, isActive, () => {
        this.selectedStablecoin = coin.id;
        this.renderDetail();
      }));
      sx += coinBtnW + 6;
    }
    y += 42;

    this.addDivider(y); y += 10;

    /* Summary */
    this.addInfoRow(0, y, 'Station', meta.label, meta.color); y += 18;
    this.addInfoRow(0, y, 'Price', `$${price.toLocaleString()}`, CSSColors.gold); y += 18;
    this.addInfoRow(0, y, 'Chain', CHAINS.find(c => c.id === this.selectedChain)!.label, '#aaccdd'); y += 18;
    this.addInfoRow(0, y, 'Token', this.selectedStablecoin, '#aaccdd'); y += 18;
    this.addInfoRow(0, y, 'Spawns In', 'Inventory (deploy later)', '#55cc88'); y += 26;

    /* Confirm button */
    if (this.isPurchasing) {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 38, 'PROCESSING...', 0x333333, 0x555555, () => {}));
    } else {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 38,
        `Confirm — $${price.toLocaleString()}`, 0x115522, 0x22aa44,
        () => this.executePurchase(),
      ));
    }
  }

  /* ── Asteroids view ────────────────────────────────────── */

  private renderAsteroidsView(): void {
    let y = 0;

    this.detailContainer.add(this.createSmallBtn(0, y, 90, 28, '← Back', () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 38;

    this.detailContainer.add(this.scene.add.text(0, y, `STORED ASTEROIDS (${this.cachedAsteroids.length})`, {
      fontSize: FontSize.md, color: '#ffd700', fontFamily: Fonts.heading,
    }));
    y += 26;

    if (this.cachedAsteroids.length === 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'No asteroids stored at this station.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }));
      return;
    }

    // Total value
    const totalValue = this.cachedAsteroids.reduce((sum, a) => sum + (a.rewardUsd || 0), 0);
    if (totalValue > 0) {
      this.detailContainer.add(this.scene.add.text(0, y, `Total Value: $${totalValue.toLocaleString()}`, {
        fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.heading,
      }));
      y += 20;
    }

    this.addDivider(y); y += 8;

    for (const asteroid of this.cachedAsteroids) {
      const CARD_H = 46;
      const card = this.scene.add.container(0, y);

      const cardBg = this.scene.add.graphics();
      cardBg.fillStyle(0x161630, 0.8);
      cardBg.fillRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
      cardBg.lineStyle(1, Colors.cyan, 0.06);
      cardBg.strokeRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
      card.add(cardBg);

      const variant = (asteroid.variant || 'unknown').replace(/_/g, ' ');
      card.add(this.scene.add.text(10, 6, variant, {
        fontSize: FontSize.sm, color: '#ffd700', fontFamily: Fonts.heading,
      }));

      // Value + deposit date
      const deposited = new Date(asteroid.depositedAt).toLocaleDateString();
      card.add(this.scene.add.text(10, 26, `Deposited: ${deposited}`, {
        fontSize: '10px', color: '#667788', fontFamily: Fonts.body,
      }));

      if (asteroid.rewardUsd > 0) {
        card.add(this.scene.add.text(this.detailW - 70, 6, `$${asteroid.rewardUsd.toLocaleString()}`, {
          fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
        }).setOrigin(0, 0));
      }

      // Retrieve button
      const retrieveBtn = this.createSmallBtn(this.detailW - 65, 26, 55, 16, 'Retrieve', () => {
        this.handleRetrieve(asteroid.id);
      });
      card.add(retrieveBtn);

      this.detailContainer.add(card);
      y += CARD_H + 5;
    }
  }

  /* ── Set Fee view ──────────────────────────────────────── */

  private renderSetFeeView(): void {
    let y = 0;

    this.detailContainer.add(this.createSmallBtn(0, y, 90, 28, '← Back', () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 38;

    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, 'SET STATION FEE', {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 28;

    this.addDivider(y); y += 14;

    // Explanation
    this.detailContainer.add(this.scene.add.text(0, y,
      'The station fee is charged when Cryptoids stored here are delivered to Earth. Range: 0% – 50%.', {
      fontSize: FontSize.xs, color: '#778899', fontFamily: Fonts.body,
      wordWrap: { width: this.detailW },
      lineSpacing: 3,
    }));
    y += 44;

    // Current value display
    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, `${this.pendingFee}%`, {
      fontSize: '36px', color: CSSColors.gold, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 52;

    // +/- buttons
    const btnW = 60;
    const btnH = 34;
    const gap = 12;
    const totalW = btnW * 2 + gap;
    const startX = (this.detailW - totalW) / 2;

    // Minus
    this.detailContainer.add(this.createActionBtn(startX, y, btnW, btnH, '− 5%',
      this.pendingFee <= 0 ? 0x222233 : 0x553333, this.pendingFee <= 0 ? 0x333344 : 0x884444,
      () => {
        if (this.pendingFee > 0) {
          this.pendingFee = Math.max(0, this.pendingFee - 5);
          this.renderDetail();
        }
      },
    ));

    // Plus
    this.detailContainer.add(this.createActionBtn(startX + btnW + gap, y, btnW, btnH, '+ 5%',
      this.pendingFee >= 50 ? 0x222233 : 0x335533, this.pendingFee >= 50 ? 0x333344 : 0x448844,
      () => {
        if (this.pendingFee < 50) {
          this.pendingFee = Math.min(50, this.pendingFee + 5);
          this.renderDetail();
        }
      },
    ));
    y += btnH + 20;

    // Current vs pending indicator
    if (this.selectedStation) {
      const currentFee = this.selectedStation.stationFeePct;
      if (this.pendingFee !== currentFee) {
        this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, `Current: ${currentFee}% → New: ${this.pendingFee}%`, {
          fontSize: FontSize.xs, color: '#aaccdd', fontFamily: Fonts.body,
        }).setOrigin(0.5, 0));
      } else {
        this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, 'No change', {
          fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
        }).setOrigin(0.5, 0));
      }
      y += 26;
    }

    // Apply button
    const canApply = this.selectedStation && this.pendingFee !== this.selectedStation.stationFeePct;
    this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 36,
      canApply ? `Apply ${this.pendingFee}% Fee` : 'No Changes',
      canApply ? 0x115522 : 0x222233,
      canApply ? 0x22aa44 : 0x333344,
      () => {
        if (canApply) this.handleSetFee(this.pendingFee);
      },
    ));
  }

  /* ── Minerals view ──────────────────────────────────────── */

  private renderMineralsView(): void {
    if (!this.selectedStation) return;
    const station = this.selectedStation;
    let y = 0;

    this.detailContainer.add(this.createSmallBtn(0, y, 90, 28, '← Back', () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 38;

    const totalMinerals = this.cachedMinerals.reduce((sum, m) => sum + m.amount, 0);
    this.detailContainer.add(this.scene.add.text(0, y, `MINERALS (${Math.floor(totalMinerals)})`, {
      fontSize: FontSize.md, color: '#55cc88', fontFamily: Fonts.heading,
    }));
    y += 26;

    // Check for docked ships at this station
    const stateMgr = ServiceLocator.tryGet<MMOStateManager>(Services.MMO_STATE);
    const dockedUnits = stateMgr ? stateMgr.getDockedUnitsAtPlanet(station.id) : [];
    const dockedShip = dockedUnits.length > 0 ? dockedUnits[0] : null;

    if (dockedShip) {
      const cargoFree = (dockedShip.cargoCapacity || 100) - (dockedShip.cargoUsed || 0);
      this.detailContainer.add(this.scene.add.text(0, y,
        `🚀 ${dockedShip.type} docked · Cargo ${dockedShip.cargoUsed || 0}/${dockedShip.cargoCapacity || 100}`, {
        fontSize: FontSize.xs, color: cargoFree > 0 ? '#55aacc' : '#ff6644', fontFamily: Fonts.body,
      }));
      y += 18;
    }

    if (this.cachedMinerals.length === 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'No minerals stored at this station.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }));
      y += 28;
    } else {
      const RARITY_COLORS: Record<string, string> = {
        common: '#aaaaaa', uncommon: '#4488ff', rare: '#ffcc00',
        epic: '#cc66ff', legendary: '#ff8800',
      };

      for (const mineral of this.cachedMinerals) {
        const hasLoad = dockedShip !== null;
        const CARD_H = hasLoad ? 46 : 36;
        const card = this.scene.add.container(0, y);

        const cardBg = this.scene.add.graphics();
        cardBg.fillStyle(0x161630, 0.8);
        cardBg.fillRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
        cardBg.lineStyle(1, Colors.cyan, 0.06);
        cardBg.strokeRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
        card.add(cardBg);

        // Color dot
        const dot = this.scene.add.graphics();
        const mineralHex = mineral.color ? parseInt(mineral.color.replace('#', ''), 16) : 0x888888;
        dot.fillStyle(mineralHex, 1);
        dot.fillCircle(14, 18, 6);
        card.add(dot);

        // Name
        const rarityColor = RARITY_COLORS[mineral.rarity] || '#aaaaaa';
        card.add(this.scene.add.text(28, 4, mineral.displayName || mineral.mineralName, {
          fontSize: FontSize.sm, color: rarityColor, fontFamily: Fonts.body,
        }));

        // Rarity label
        card.add(this.scene.add.text(28, 19, mineral.rarity, {
          fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
        }));

        // Quantity (right)
        card.add(this.scene.add.text(this.detailW - 8, 10, `x${Math.floor(mineral.amount).toLocaleString()}`, {
          fontSize: FontSize.sm, color: '#aaccdd', fontFamily: Fonts.heading,
        }).setOrigin(1, 0.5));

        // Load buttons (only when a ship is docked)
        if (hasLoad) {
          const maxLoad = Math.floor(mineral.amount);
          const handleLoad = async (qty: number) => {
            if (!dockedShip) return;
            this.setStatus('Loading...');
            const result = await this.monetization.loadCargoFromLocation(
              dockedShip.id, 'station', station.id, mineral.mineralTypeId, qty,
            );
            if (result.success) {
              const name = mineral.displayName || mineral.mineralName;
              this.setStatus(`Loaded ${result.loaded} ${name} (cargo ${result.cargoUsed}/${result.cargoCapacity})`);
              EventBus.emit(MMOEvents.CARGO_LOADED, {
                mineralName: name, amount: result.loaded, shipType: dockedShip.type,
              });
              this.cachedMinerals = await this.monetization.fetchLocationMinerals('station', station.id);
              const newTotal = this.cachedMinerals.reduce((s, m) => s + m.amount, 0);
              this.stationMineralCounts.set(station.id, Math.floor(newTotal));
              this.renderDetail();
              this.renderList();
            } else {
              this.setStatus(result.error || 'Load failed');
            }
          };

          const btnY = CARD_H - 20;
          const btnH = 18;
          let bx = this.detailW;
          // All button
          const allW = 38;
          bx -= allW;
          card.add(this.createSmallBtn(bx, btnY, allW, btnH, 'All', () => handleLoad(maxLoad)));
          // x10 button
          if (maxLoad >= 10) {
            bx -= 32;
            card.add(this.createSmallBtn(bx, btnY, 30, btnH, '10', () => handleLoad(10)));
          }
          // x1 button
          bx -= 26;
          card.add(this.createSmallBtn(bx, btnY, 24, btnH, '1', () => handleLoad(1)));

          // "Load" label
          card.add(this.scene.add.text(bx - 4, btnY + 2, 'Load:', {
            fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
          }).setOrigin(1, 0));
        }

        this.detailContainer.add(card);
        y += CARD_H + 4;
      }
    }

    y += 8;
    this.addDivider(y); y += 10;

    // Active transfers for this station
    const stationTransfers = this.cachedTransfers.filter(
      (t: any) => t.status === 'active' &&
        (t.fromLocationId === station.id || t.toLocationId === station.id),
    );

    if (stationTransfers.length > 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'ACTIVE TRANSFERS', {
        fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
      }));
      y += 18;

      for (const transfer of stationTransfers) {
        const isOutgoing = transfer.fromLocationId === station.id;
        const dir = isOutgoing ? '→' : '←';
        const otherName = isOutgoing ? (transfer.toLocationName || transfer.toLocationId) : (transfer.fromLocationName || transfer.fromLocationId);
        const remaining = transfer.totalMinerals - transfer.transferredMinerals;
        const eta = transfer.estimatedTicksRemaining || remaining;

        const CARD_H = 44;
        const card = this.scene.add.container(0, y);

        const bg = this.scene.add.graphics();
        bg.fillStyle(isOutgoing ? 0x332211 : 0x112233, 0.8);
        bg.fillRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
        bg.lineStyle(1, isOutgoing ? 0x886644 : 0x446688, 0.15);
        bg.strokeRoundedRect(0, 0, this.detailW, CARD_H, Radius.sm);
        card.add(bg);

        card.add(this.scene.add.text(8, 5, `${dir} ${otherName}`, {
          fontSize: FontSize.xs, color: isOutgoing ? '#ffaa66' : '#66aaff', fontFamily: Fonts.heading,
        }));

        card.add(this.scene.add.text(8, 22, `${transfer.transferredMinerals}/${transfer.totalMinerals} · ETA ~${eta}s`, {
          fontSize: '10px', color: '#778899', fontFamily: Fonts.body,
        }));

        // Cancel button (only for outgoing)
        if (isOutgoing) {
          const cancelBtn = this.createSmallBtn(this.detailW - 58, 8, 50, 22, 'Cancel', async () => {
            this.setStatus('Cancelling...');
            const result = await this.monetization.cancelMineralTransfer(transfer.batchId);
            if (result.success) {
              this.setStatus('Transfer cancelled');
              this.cachedMinerals = await this.monetization.fetchLocationMinerals('station', station.id);
              this.cachedTransfers = await this.monetization.fetchMineralTransfers();
              this.renderDetail();
            } else {
              this.setStatus(result.error || 'Cancel failed');
            }
          });
          card.add(cancelBtn);
        }

        this.detailContainer.add(card);
        y += CARD_H + 4;
      }
      y += 4;
    }

    // Transfer button (only if station has minerals)
    if (this.cachedMinerals.length > 0) {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Transfer Minerals →', 0x1a2a44, 0x335577, () => {
        this.transferTargetId = null;
        this.detailView = 'transfer';
        this.renderDetail();
      }));
    }

    // Auto-refresh every 5s while active transfers exist for this station
    this.stopRefreshTimer();
    if (stationTransfers.length > 0) {
      this.refreshTimer = this.scene.time.addEvent({
        delay: 5000,
        loop: true,
        callback: async () => {
          if (this.detailView !== 'minerals' || !this.selectedStation) {
            this.stopRefreshTimer();
            return;
          }
          this.cachedMinerals = await this.monetization.fetchLocationMinerals('station', this.selectedStation.id);
          this.cachedTransfers = await this.monetization.fetchMineralTransfers();
          // Update the mineral count in our cache too
          const newTotal = this.cachedMinerals.reduce((s, m) => s + m.amount, 0);
          this.stationMineralCounts.set(this.selectedStation.id, Math.floor(newTotal));
          // Stop refreshing if no more active transfers
          const stillActive = this.cachedTransfers.some(
            (t: any) => t.status === 'active' &&
              (t.fromLocationId === this.selectedStation!.id || t.toLocationId === this.selectedStation!.id),
          );
          if (!stillActive) this.stopRefreshTimer();
          this.renderDetail();
        },
      });
    }
  }

  /* ── Transfer view — pick destination station ─────────── */

  private renderTransferView(): void {
    if (!this.selectedStation) return;
    const station = this.selectedStation;
    let y = 0;

    this.detailContainer.add(this.createSmallBtn(0, y, 90, 28, '← Back', () => {
      this.detailView = 'minerals';
      this.renderDetail();
    }));
    y += 38;

    this.detailContainer.add(this.scene.add.text(0, y, 'TRANSFER FROM', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 16;

    const meta = STATION_META[station.stationType] || { label: station.stationType, color: '#ffffff' };
    this.detailContainer.add(this.scene.add.text(0, y, `${meta.label} (${station.positionQ}, ${station.positionR})`, {
      fontSize: FontSize.sm, color: meta.color as string, fontFamily: Fonts.heading,
    }));
    y += 22;

    const totalCount = this.cachedMinerals.reduce((s, m) => s + m.amount, 0);
    this.detailContainer.add(this.scene.add.text(0, y, `${Math.floor(totalCount)} minerals · 1/tick`, {
      fontSize: FontSize.xs, color: '#778899', fontFamily: Fonts.body,
    }));
    y += 20;

    this.addDivider(y); y += 10;

    this.detailContainer.add(this.scene.add.text(0, y, 'SELECT DESTINATION', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    // Filter out current station and non-active stations
    const targets = this.stations.filter(s => s.id !== station.id && s.status === 'active');

    // Always show Earth (planet_earth)
    const earthSelected = this.transferTargetId === 'planet_earth';
    const earthCard = this.scene.add.container(0, y);
    const earthBg = this.scene.add.graphics();
    earthBg.fillStyle(earthSelected ? 0x253a66 : 0x151525, 0.9);
    earthBg.fillRoundedRect(0, 0, this.detailW, 34, Radius.sm);
    earthBg.lineStyle(1, earthSelected ? 0x44aaff : 0x334455, earthSelected ? 0.4 : 0.1);
    earthBg.strokeRoundedRect(0, 0, this.detailW, 34, Radius.sm);
    earthCard.add(earthBg);
    earthCard.add(this.scene.add.text(10, 8, 'Earth (Home)', {
      fontSize: FontSize.sm, color: earthSelected ? '#44ff88' : '#aaccdd', fontFamily: Fonts.heading,
    }));
    if (earthSelected) {
      earthCard.add(this.scene.add.text(this.detailW - 10, 8, '✓', {
        fontSize: FontSize.sm, color: '#44ff88', fontFamily: Fonts.heading,
      }).setOrigin(1, 0));
    }
    const earthZone = this.scene.add.zone(0, 0, this.detailW, 34).setOrigin(0, 0);
    earthZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, 34), Phaser.Geom.Rectangle.Contains);
    earthZone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    earthZone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
    earthZone.on('pointerdown', () => {
      this.transferTargetId = 'planet_earth';
      this.renderDetail();
    });
    earthCard.add(earthZone);
    this.detailContainer.add(earthCard);
    y += 38;

    // Other stations
    for (const target of targets) {
      const tmeta = STATION_META[target.stationType] || { label: target.stationType, color: '#ffffff' };
      const isSelected = this.transferTargetId === target.id;
      const card = this.scene.add.container(0, y);

      const bg = this.scene.add.graphics();
      bg.fillStyle(isSelected ? 0x253a66 : 0x151525, 0.9);
      bg.fillRoundedRect(0, 0, this.detailW, 34, Radius.sm);
      bg.lineStyle(1, isSelected ? 0x44aaff : 0x334455, isSelected ? 0.4 : 0.1);
      bg.strokeRoundedRect(0, 0, this.detailW, 34, Radius.sm);
      card.add(bg);

      card.add(this.scene.add.text(10, 4, tmeta.label, {
        fontSize: FontSize.sm, color: isSelected ? (tmeta.color as string) : '#aaccdd', fontFamily: Fonts.heading,
      }));
      card.add(this.scene.add.text(10, 19, `(${target.positionQ}, ${target.positionR})`, {
        fontSize: '10px', color: '#667788', fontFamily: Fonts.body,
      }));

      if (isSelected) {
        card.add(this.scene.add.text(this.detailW - 10, 8, '✓', {
          fontSize: FontSize.sm, color: '#44ff88', fontFamily: Fonts.heading,
        }).setOrigin(1, 0));
      }

      const zone = this.scene.add.zone(0, 0, this.detailW, 34).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, 34), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
      zone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
      zone.on('pointerdown', () => {
        this.transferTargetId = target.id;
        this.renderDetail();
      });
      card.add(zone);

      this.detailContainer.add(card);
      y += 38;
    }

    if (targets.length === 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'No other active stations.', {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
      }));
      y += 20;
    }

    y += 8;

    // ETA estimate
    if (this.transferTargetId) {
      const etaSecs = Math.ceil(totalCount);
      const etaMin = Math.floor(etaSecs / 60);
      const etaLabel = etaMin > 0 ? `~${etaMin}m ${etaSecs % 60}s` : `~${etaSecs}s`;
      this.detailContainer.add(this.scene.add.text(0, y, `ETA: ${etaLabel} (${Math.floor(totalCount)} minerals at 1/tick)`, {
        fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
      }));
      y += 22;
    }

    // Confirm button
    const canTransfer = this.transferTargetId !== null && totalCount > 0;
    this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 36,
      canTransfer ? 'Start Transfer' : 'Select Destination',
      canTransfer ? 0x115522 : 0x222233,
      canTransfer ? 0x22aa44 : 0x333344,
      async () => {
        if (!canTransfer || !this.transferTargetId) return;
        this.setStatus('Starting transfer...');

        const toType = this.transferTargetId === 'planet_earth' ? 'planet' : 'station';
        const toId = this.transferTargetId;
        const result = await this.monetization.initiateTransfer('station', station.id, toType, toId);

        if (result.success) {
          this.setStatus(`Transfer started! ${result.totalMinerals} minerals, ~${result.estimatedTicks}s`);
          this.cachedMinerals = await this.monetization.fetchLocationMinerals('station', station.id);
          this.cachedTransfers = await this.monetization.fetchMineralTransfers();
          this.detailView = 'minerals';
          this.renderDetail();
        } else {
          this.setStatus(result.error || 'Transfer failed');
        }
      },
    ));
  }

  /* ================================================================
     ACTIONS
     ================================================================ */

  private async executePurchase(): Promise<void> {
    if (this.isPurchasing) return;
    const type = this.purchaseType;
    const meta = STATION_META[type];
    const price = this.prices[type] ?? 0;

    this.isPurchasing = true;
    this.renderDetail();
    this.setStatus(`Purchasing ${meta.label}...`);

    // Use PaymentFlowManager if available, otherwise fall back to monetization manager
    const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
    let result: { success: boolean; error?: string };

    if (paymentFlow) {
      result = await paymentFlow.executePayment({
        assetType: 'station',
        amountUsd: price,
        chain: this.selectedChain,
        stablecoin: this.selectedStablecoin,
        metadata: { stationType: type },
      });
    } else {
      result = await this.monetization.purchaseStation(type, this.selectedChain, this.selectedStablecoin);
    }

    this.isPurchasing = false;
    if (result.success) {
      this.setStatus(`${meta.label} purchased! Check your inventory.`);
      this.detailView = 'info';
      this.loadData();
    } else {
      this.setStatus(result.error || 'Purchase failed');
      this.renderDetail();
    }
  }

  private async handleDeploy(): Promise<void> {
    if (!this.selectedStation) return;
    this.setStatus('Select a location on the map to deploy. For now, deploying near Earth...');
    const q = Math.floor(Math.random() * 5) + 1;
    const r = Math.floor(Math.random() * 5) + 1;
    const result = await this.monetization.deployStation(this.selectedStation.id, q, r);
    if (result.success) {
      this.setStatus(`Station deployed at (${q}, ${r})!`);
      this.loadData();
    } else {
      this.setStatus(result.error || 'Deploy failed');
    }
  }

  private async handleSetFee(feePct: number): Promise<void> {
    if (!this.selectedStation) return;
    this.setStatus(`Setting fee to ${feePct}%...`);
    const result = await this.monetization.setStationFee(this.selectedStation.id, feePct);
    if (result.success) {
      this.setStatus(`Fee set to ${feePct}%`);
      this.detailView = 'info';
      this.loadData();
    } else {
      this.setStatus(result.error || 'Failed to set fee');
    }
  }

  private async handleRetrieve(depositId: string): Promise<void> {
    if (!this.selectedStation) return;
    this.setStatus('Retrieving asteroid...');
    const result = await this.monetization.retrieveAsteroid(this.selectedStation.id, depositId);
    if (result.success) {
      this.setStatus(`Asteroid retrieved! (${result.variant})`);
      // Refresh asteroids view
      this.cachedAsteroids = await this.monetization.fetchStationAsteroids(this.selectedStation.id);
      this.renderDetail();
    } else {
      this.setStatus(result.error || 'Retrieval failed');
    }
  }

  /* ================================================================
     UI HELPERS
     ================================================================ */

  private createMobileBackBtn(): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(0, 0);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e2844, 0.9);
    bg.fillRoundedRect(0, 0, 80, 26, 4);
    bg.lineStyle(1, 0x00ffff, 0.1);
    bg.strokeRoundedRect(0, 0, 80, 26, 4);
    btn.add(bg);
    btn.add(this.scene.add.text(40, 13, '\u2190 Back', {
      fontSize: '12px', color: '#8899bb', fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5));
    const zone = this.scene.add.zone(0, 0, 80, 26).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 26), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', () => {
      this.listContainer.setVisible(true);
      this.detailContainer.setVisible(false);
    });
    btn.add(zone);
    return btn;
  }

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.06);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  /** Stat row with dotted leader + hover highlight */
  private addStatRow(x: number, y: number, label: string, value: string, valueColor: string): number {
    const ROW_H = 20;
    const row = this.scene.add.container(x, y);

    const labelText = this.scene.add.text(0, 0, label, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    row.add(labelText);

    const valText = this.scene.add.text(this.detailW, 0, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    }).setOrigin(1, 0);
    row.add(valText);

    // Dotted leader
    const dotStart = labelText.width + 4;
    const dotEnd = this.detailW - valText.width - 4;
    const dots = this.scene.add.text(dotStart, 0, '', {
      fontSize: FontSize.xs, color: '#334455', fontFamily: Fonts.body,
    });
    let dotStr = '';
    while (dots.width < dotEnd - dotStart) { dotStr += ' ·'; dots.setText(dotStr); }
    row.add(dots);

    // Hover highlight
    const hoverBg = this.scene.add.graphics();
    row.addAt(hoverBg, 0);
    const hitZone = this.scene.add.zone(0, 0, this.detailW, ROW_H).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, ROW_H), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      hoverBg.clear();
      hoverBg.fillStyle(0xffffff, 0.03);
      hoverBg.fillRoundedRect(-2, -2, this.detailW + 4, ROW_H + 2, 2);
    });
    hitZone.on('pointerout', () => { hoverBg.clear(); });
    row.add(hitZone);

    this.detailContainer.add(row);
    return y + ROW_H;
  }

  /** Simple label: value row */
  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string = '#aaccdd'): void {
    this.detailContainer.add(this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    }));
    const labelW = this.scene.add.text(0, 0, `${label}: `, { fontSize: FontSize.xs, fontFamily: Fonts.body }).width;
    this.detailContainer.add(this.scene.add.text(x + labelW, y, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    }));
  }


  /** Full-width action button with Zone hitbox */
  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, fillColor: number, borderColor: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      const c = hover ? Phaser.Display.Color.IntegerToColor(fillColor).brighten(20).color : fillColor;
      bg.fillStyle(c, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, borderColor, 0.6);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    };
    draw(false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: '#ffffff', fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { draw(true); this.scene.input.setDefaultCursor('pointer'); });
    zone.on('pointerout', () => { draw(false); this.scene.input.setDefaultCursor('default'); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }

  /** Small button (Back, etc.) */
  private createSmallBtn(
    x: number, y: number, w: number, h: number,
    label: string, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? 0x2a3a5a : 0x1e2844, 0.9);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, hover ? 0.2 : 0.1);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    };
    draw(false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: '#8899bb', fontFamily: Fonts.body,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { draw(true); this.scene.input.setDefaultCursor('pointer'); });
    zone.on('pointerout', () => { draw(false); this.scene.input.setDefaultCursor('default'); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }

  /** Toggle button for chain / stablecoin selection */
  private createToggleBtn(
    x: number, y: number, w: number, h: number,
    label: string, active: boolean, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (isActive: boolean, hover: boolean) => {
      bg.clear();
      if (isActive) {
        bg.fillStyle(Colors.cyan, 1);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
        bg.lineStyle(1, 0x66ccff, 0.6);
        bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      } else {
        bg.fillStyle(hover ? 0x2a3a5a : 0x1e2844, 0.8);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
        bg.lineStyle(1, hover ? 0x445577 : 0x334466, 0.4);
        bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      }
    };
    draw(active, false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs,
      color: active ? '#000000' : '#99aacc',
      fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => {
      if (!active) draw(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    zone.on('pointerout', () => {
      draw(active, false);
      this.scene.input.setDefaultCursor('default');
    });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }
}
